import React, { useMemo, useState } from 'react';
import { useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { Modal } from '../../components/common/Modal';
import { CreateSalesOrderForm } from './CreateSalesOrderForm';
import type { SalesOrder, SalesOrderFormData, SalesOrderPaymentStatus } from '../../types/salesOrder';
import { salesOrderService } from '../../services/salesOrderService';
import { useSettingsStore } from '../../store/settingsStore';
import { LoadingSpinner } from '../../components/common/LoadingSpinner';
import { useToast } from '../../components/common/Toast';
import { SalesOrderPaymentModal } from './components/SalesOrderPaymentModal';
import { CustomerForm } from '../selling/CustomerForm';
import { customerService } from '../../services/customerService';
import { type Customer } from '../../types/customer';
import { useLanguage } from '../../features/language/useLanguage';
import { groupItemsByDate } from '../../utils/date';
import { useSort } from '../../hooks/useSort';
import { usePermissions } from '../../hooks/usePermissions';
import { Pagination } from '../../components/common/Pagination';
import { usePaginationState } from '../../hooks/usePaginationState';
import { SalesOrderFilters } from './components/SalesOrderFilters';
import { SalesOrderMobileList } from './components/SalesOrderMobileList';
import { SalesOrderTable } from './components/SalesOrderTable';
import { useOnboardingStore } from '../../store/onboardingStore';

export const SalesOrderList: React.FC = () => {
  const { t } = useLanguage();
  const { can } = usePermissions();
  const canWrite = can('sales_orders', 'write');
  // True only while the onboarding tour is on step 6 (selling practice).
  // Used to tag the practice order and dispatch the delivered event.
  const onboardingStep6 = useOnboardingStore(
    (s) => s.state?.status === 'in_progress' && s.state?.currentStep === 6,
  );
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const { currencySymbol } = useSettingsStore();
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [editingSO, setEditingSO] = useState<SalesOrder | null>(null);
  const [payingSO, setPayingSO] = useState<SalesOrder | null>(null);
  const [isPaymentSaving, setIsPaymentSaving] = useState(false);
  const [isPaymentError, setIsPaymentError] = useState(false);
  const [viewingCustomer, setViewingCustomer] = useState<Customer | null>(null);
  const [isCustomerModalOpen, setIsCustomerModalOpen] = useState(false);
  const [searchFilter, setSearchFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [orderStartDateFilter, setOrderStartDateFilter] = useState('');
  const [orderEndDateFilter, setOrderEndDateFilter] = useState('');
  const [createModalSession, setCreateModalSession] = useState(0);
  const [editModalSession, setEditModalSession] = useState(0);

  const { page, size, setPage, setSize, resetOnChange } = usePaginationState({ defaultSize: 25 });
  resetOnChange([searchFilter, statusFilter, orderStartDateFilter, orderEndDateFilter]);

  const { data: sosResponse, isLoading: loading, error: queryError } = useQuery({
    queryKey: ['sales-orders', { page, size, search: searchFilter, status: statusFilter }],
    queryFn: () => salesOrderService.list({ page, size, search: searchFilter, status: statusFilter }),
    placeholderData: keepPreviousData,
    staleTime: 5 * 60 * 1000,
  });

  const sos = useMemo(() => sosResponse?.data ?? [], [sosResponse?.data]);
  const total = sosResponse?.total ?? 0;

  const error = queryError ? t('salesOrder.list.errorLoad') : null;

  const handleCreateSO = async (data: SalesOrderFormData) => {
    try {
      // During onboarding step 6, tag the order so the backend marks it as the
      // practice sample (gates stock deduction + filters it from non-onboarding views).
      await salesOrderService.create({ ...data, onboardingSample: onboardingStep6 });
      await queryClient.invalidateQueries({ queryKey: ['sales-orders'] });
      await queryClient.invalidateQueries({ queryKey: ['reports_receivables'] });
      setIsCreateModalOpen(false);
    } catch (err: unknown) {
      console.error('Failed to create SO:', err);
      // Let the form handle the error display or rethrow if needed
      throw err;
    }
  };

  const handleUpdateSO = async (data: SalesOrderFormData) => {
    if (!editingSO) return;
    try {
      await salesOrderService.update(editingSO.id, data);
      await queryClient.invalidateQueries({ queryKey: ['sales-orders'] });
      // During onboarding step 6: notify the guide overlay when the practice order
      // reaches Delivered so it can advance the tour. Fire on data.status alone —
      // openMarkAs() pre-sets editingSO.status to the target ('Delivered'), so a
      // "was it already delivered?" guard against editingSO would always be false
      // here and the event would never fire. Re-firing is harmless: advancing past
      // step 6 flips onboardingStep6 off, and advance() is idempotent.
      if (onboardingStep6 && data.status === 'Delivered') {
        window.dispatchEvent(
          new CustomEvent('qrema:sales-order-delivered', { detail: { soId: editingSO.id } }),
        );
      }
      await queryClient.invalidateQueries({ queryKey: ['reports_receivables'] });
    } catch (err: unknown) {
      console.error('Failed to update SO:', err);
      throw err;
    }
  };

  // The paginated list projection is a lightweight summary and omits several
  // editable fields (message_on_invoice, internal_notes, terms, ship_via,
  // tracking_number, tags, item descriptions, ...). Editing straight from a
  // list row would show them blank and wipe them on save. Hydrate the full
  // record via getById (single source of truth) before opening the edit modal.
  const openEditSO = async (so: SalesOrder) => {
    setEditModalSession((prev) => prev + 1);
    const full = await salesOrderService.getById(so.id, { fresh: true });
    setEditingSO(full ?? so);
  };

  const openMarkAs = async (so: SalesOrder, status: SalesOrder['status']) => {
    setEditModalSession((prev) => prev + 1);
    const full = await salesOrderService.getById(so.id, { fresh: true });
    setEditingSO({ ...(full ?? so), status });
  };

  const openPay = (so: SalesOrder, paymentStatus?: SalesOrderPaymentStatus) => {
    setIsPaymentError(false);
    setPayingSO(paymentStatus ? { ...so, paymentStatus } : so);
  };

  const handlePaySubmit = async (payment: {
    paymentStatus: SalesOrderPaymentStatus;
    paymentType: string;
    paymentProofUrl: string | null;
  }) => {
    if (!payingSO) return;
    setIsPaymentSaving(true);
    setIsPaymentError(false);
    try {
      await salesOrderService.updatePayment(payingSO.id, payment);
      await queryClient.invalidateQueries({ queryKey: ['sales-orders'] });
      await queryClient.invalidateQueries({ queryKey: ['reports_receivables'] });
      showToast(t('salesOrder.payment.toast.saved'), 'success');
      setPayingSO(null);
    } catch (err) {
      console.error('Failed to update SO payment:', err);
      setIsPaymentError(true);
      showToast(t('salesOrder.payment.toast.failed'), 'error');
    } finally {
      setIsPaymentSaving(false);
    }
  };

  const handleCustomerClick = async (customerName: string) => {
    if (!customerName) return;
    try {
      const customers = await customerService.getAll();
      const customer = customers.find((c) => c.name === customerName);
      if (customer) {
        setViewingCustomer(customer);
        setIsCustomerModalOpen(true);
      }
    } catch (err) {
      console.error('Failed to load customer details:', err);
    }
  };

  // Server handles search + status filtering. Date filter is applied client-side
  // on the current page only — backend support for order_date range is pending.
  const filteredSOs = useMemo(() => {
    if (!orderStartDateFilter && !orderEndDateFilter) return sos;
    const startDateTime = orderStartDateFilter ? new Date(`${orderStartDateFilter}T00:00:00`).getTime() : null;
    const endDateTime = orderEndDateFilter ? new Date(`${orderEndDateFilter}T23:59:59.999`).getTime() : null;
    return sos.filter((so) => {
      const orderDateTime = new Date(so.orderDate).getTime();
      const matchesStart = startDateTime === null || (!Number.isNaN(orderDateTime) && orderDateTime >= startDateTime);
      const matchesEnd = endDateTime === null || (!Number.isNaN(orderDateTime) && orderDateTime <= endDateTime);
      return matchesStart && matchesEnd;
    });
  }, [sos, orderStartDateFilter, orderEndDateFilter]);

  const { sortedItems: sortedSOs, sortConfig, handleSort } = useSort(filteredSOs, {
    key: 'orderDate',
    direction: 'desc',
  });

  const shouldGroupByDate = sortConfig?.key === 'orderDate';
  const groupedSOs = useMemo(() => groupItemsByDate(sortedSOs, (so) => so.orderDate), [sortedSOs]);
  const sortedDates = useMemo(() => Object.keys(groupedSOs).sort((a, b) => b.localeCompare(a)), [groupedSOs]);

  if (loading && sos.length === 0) {
    return <LoadingSpinner text={t('salesOrder.list.loading')} />;
  }

  // Shared row/card context for both the mobile list and desktop table.
  const rowActions = {
    canWrite,
    currencySymbol,
    onOpen: openEditSO,
    onCustomerClick: handleCustomerClick,
    onMarkAs: openMarkAs,
    onPay: openPay,
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <h1 className="text-2xl font-semibold text-gray-900">{t('salesOrder.list.title')}</h1>
        {canWrite && (
          <button
            data-test-id="so-create-button"
            onClick={() => {
              setCreateModalSession((prev) => prev + 1);
              setIsCreateModalOpen(true);
            }}
            className="w-full sm:w-auto px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {t('salesOrder.list.newButton')}
          </button>
        )}
      </div>

      {error && <div className="p-4 bg-red-100 text-red-700 rounded-md">{error}</div>}

      <div className="bg-white p-4 rounded-lg shadow-sm">
        <SalesOrderFilters
          search={searchFilter}
          onSearchChange={setSearchFilter}
          status={statusFilter}
          onStatusChange={setStatusFilter}
          startDate={orderStartDateFilter}
          endDate={orderEndDateFilter}
          onApplyDateRange={(start, end) => {
            setOrderStartDateFilter(start);
            setOrderEndDateFilter(end);
          }}
        />

        <SalesOrderMobileList
          orders={sortedSOs}
          isEmpty={filteredSOs.length === 0}
          grouped={shouldGroupByDate}
          groupedOrders={groupedSOs}
          sortedDates={sortedDates}
          {...rowActions}
        />

        <Pagination
          page={page}
          pageSize={size}
          total={total}
          onPageChange={setPage}
          onPageSizeChange={setSize}
          isLoading={loading}
          className="md:hidden"
        />

        <SalesOrderTable
          orders={sortedSOs}
          isEmpty={filteredSOs.length === 0}
          grouped={shouldGroupByDate}
          groupedOrders={groupedSOs}
          sortedDates={sortedDates}
          sortKey={sortConfig?.key}
          onSort={handleSort}
          page={page}
          size={size}
          total={total}
          onPageChange={setPage}
          onPageSizeChange={setSize}
          loading={loading}
          {...rowActions}
        />
      </div>

      <Modal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        title={t('salesOrder.list.modal.createTitle')}
        maxWidth="7xl"
      >
        <CreateSalesOrderForm
          key={`so-create-${createModalSession}`}
          isOpen={isCreateModalOpen}
          onSubmit={handleCreateSO}
          onCancel={() => setIsCreateModalOpen(false)}
          readOnly={!canWrite}
        />
      </Modal>

      <Modal
        isOpen={!!editingSO}
        onClose={() => {
          setEditingSO(null);
        }}
        title={t('salesOrder.list.modal.editTitle', { orderNumber: editingSO?.orderNumber ?? '' })}
        maxWidth="7xl"
      >
        {editingSO && (
          <CreateSalesOrderForm
            key={`so-edit-${editingSO.id}-${editModalSession}`}
            isOpen={!!editingSO}
            initialData={editingSO}
            onSubmit={handleUpdateSO}
            onCancel={() => {
              setEditingSO(null);
            }}
            readOnly={!canWrite}
          />
        )}
      </Modal>

      <Modal
        isOpen={!!payingSO}
        onClose={() => setPayingSO(null)}
        title={t('salesOrder.payment.title')}
        maxWidth="lg"
      >
        {payingSO && (
          <SalesOrderPaymentModal
            order={payingSO}
            currencySymbol={currencySymbol}
            isSaving={isPaymentSaving}
            isError={isPaymentError}
            readOnly={!canWrite}
            onSubmit={handlePaySubmit}
            onCancel={() => setPayingSO(null)}
          />
        )}
      </Modal>

      <Modal
        isOpen={isCustomerModalOpen}
        onClose={() => {
          setIsCustomerModalOpen(false);
          setViewingCustomer(null);
        }}
        title={t('salesOrder.list.modal.customerDetails')}
        maxWidth="2xl"
      >
        <CustomerForm
          initialData={viewingCustomer || undefined}
          onCancel={() => {
            setIsCustomerModalOpen(false);
            setViewingCustomer(null);
          }}
          readOnly={true}
        />
      </Modal>
    </div>
  );
};
